import React, { useState, useEffect } from "react";
import { ApiService } from "../../service/api.service";
import { MASTER_URL } from "../../common/common";
import { FieldErrors, UseFormRegister, UseFormSetValue } from "react-hook-form";

interface TitleOption {
  label: string;
  value: string;
}

interface TitleSelectProps {
  value: string;
  onChange: (value: string) => void;
  register: UseFormRegister<any>;
  name: string;
  errors?: FieldErrors;
  setValue: UseFormSetValue<any>;
  label?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

const TitleSelect = ({
  value,
  onChange,
  register,
  name,
  errors,
  setValue,
  label = "Select Title",
  disabled = false,
  placeholder = "Select an option",
  className = "select",
}: TitleSelectProps) => {
  const [options, setOptions] = useState<TitleOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const apiService = new ApiService();

  useEffect(() => {
    // Use static title options instead of API call for better reliability
    const staticTitleOptions = [
      { label: "select", value: "" },
      { label: "Mr.", value: "Mr." },
      { label: "Mrs.", value: "Mrs." },
      { label: "Ms.", value: "Ms." },
      { label: "Miss.", value: "Miss." },
      { label: "Dr.", value: "Dr." }
    ];

    setOptions(staticTitleOptions);
    setIsLoading(false);
  }, []);

  // Handle nested form field errors correctly
  const getNestedError = (errors: any, fieldName: string) => {
    // For nested fields like "executiveDetails[0].prefix"
    if (fieldName.includes('[') && fieldName.includes('].')) {
      const parts = fieldName.split(/[\[\]\.]+/).filter(Boolean);
      let current = errors;

      for (const part of parts) {
        if (current && typeof current === 'object') {
          current = current[part];
        } else {
          return undefined;
        }
      }

      return current?.message;
    }

    // For simple fields
    return errors?.[fieldName]?.message;
  };

  const errorMessage = getNestedError(errors, name) as string;

  return (
    <div className={`w-[80px]  mt-[32px] ${className}`}>
      {label && (
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          id={name}
          {...register(name)}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || isLoading}
          className={`w-full px-4 py-2 border rounded-lg bg-white cursor-pointer text-sm
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all
            appearance-none text-gray-900
            ${disabled || isLoading ? "opacity-50 cursor-not-allowed" : ""}
            ${errorMessage ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300"}
          `}
        >
          {isLoading ? (
            <option value="" disabled>Loading...</option>
          ) : (
            options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))
          )}
        </select>

        {/* Custom dropdown arrow */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
          <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>

      {errorMessage && <p className="text-sm text-red-600 mt-1">{errorMessage}</p>}
    </div>
  );
};

export default TitleSelect;